import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "@softmax/observatory-client/transport";
import type { StorePageEditConflict } from "../store-page/types";
import { isStorePageEditConflict } from "../store-page/edit-conflict";
import { failureMessage, invalidDraftWrite } from "./operation-failure";
import type { Operation } from "./draft-model";
type Failure = {
  operation: Operation;
  message: string;
};
type PendingOperation = {
  operation: Operation;
  replaceOnEdit: boolean;
  run: () => Promise<unknown>;
  refresh?: (() => Promise<void>) | undefined;
};
export function useStorePageOperation({
  enabled,
  onConflict,
}: {
  enabled: boolean;
  onConflict: (conflict: StorePageEditConflict) => Promise<void>;
}) {
  const [failure, setFailure] = useState<Failure | null>(null);
  const [activeOperation, setActiveOperation] = useState<Operation | null>(
    null,
  );
  const running = useRef(false);
  const pendingRetry = useRef<PendingOperation | null>(null);
  const active = useRef(false);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const runOperation = useCallback(
    async <T>({
      operation,
      run,
      refresh,
    }: {
      operation: Operation;
      run: () => Promise<T>;
      refresh?: (() => Promise<void>) | undefined;
    }): Promise<T | null> => {
      if (!enabled || !active.current || running.current) return null;

      running.current = true;
      setActiveOperation(operation);
      setFailure(null);
      let phase = operation;
      let retryRun: () => Promise<unknown> = run;
      let retryRefresh = refresh;

      try {
        const result = await run();

        if (!active.current) return result;

        if (refresh) {
          phase = "refresh";
          retryRun = refresh;
          retryRefresh = undefined;
          setActiveOperation(phase);
          await refresh();
        }

        pendingRetry.current = null;
        return result;
      } catch (error) {
        if (!active.current) return null;

        if (
          error instanceof ApiError &&
          error.status === 409 &&
          isStorePageEditConflict(error.detail)
        ) {
          await onConflict(error.detail);
          pendingRetry.current = null;
          return null;
        }

        pendingRetry.current = {
          operation: phase,
          run: retryRun,
          refresh: retryRefresh,
          replaceOnEdit: invalidDraftWrite({ operation: phase, error }),
        };
        setFailure({
          operation: phase,
          message: failureMessage({
            operation: phase,
            error,
          }),
        });
        return null;
      } finally {
        running.current = false;

        if (active.current) setActiveOperation(null);
      }
    },
    [enabled, onConflict],
  );
  const clearFailure = useCallback(() => {
    pendingRetry.current = null;
    setFailure(null);
  }, []);
  const clearInvalidWrite = useCallback(() => {
    if (pendingRetry.current?.replaceOnEdit) clearFailure();
  }, [clearFailure]);
  const retry = useCallback(async () => {
    const pending = pendingRetry.current;

    if (pending) await runOperation(pending);
  }, [runOperation]);
  return {
    failure,
    activeOperation,
    runOperation,
    retry,
    clearFailure,
    clearInvalidWrite,
  };
}
